<template>
  <form @submit.prevent="submit" class="standard-product-form uk-form-stacked">
    <!-- Basic Data -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.basicFields')}}</legend>

      <readonly-form-field
        :errors="fields.errors.name"
        :label="$t('fields.name')"
      >
        <text-placeholder :text="name" />
      </readonly-form-field>

      <product-family-combobox
        :disabled="!isEditable"
        :errors="fields.errors.family"
        :filter="{ bundle: false }"
        :label="$t('fields.family')"
        v-model="fields.values.family"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.variantName"
        :label="$t('fields.variantName')"
        :placeholder="$t('placeholders.variantName')"
        ref="variantNameField"
        type="text"
        v-model="fields.values.variantName"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.articleNumber"
        :label="$t('fields.articleNumber')"
        type="text"
        v-model="fields.values.articleNumber"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.ean"
        :label="$t('fields.ean')"
        type="text"
        v-model="fields.values.ean"
      />

      <text-input
        type="text"
        :label="$t('fields.manufacturerProductCode')"
        v-model="fields.values.manufacturerProductCode"
        :errors="fields.errors.manufacturerProductCode"
        :placeholder="$t('placeholders.manufacturerProductCode')"
        :disabled="!isEditable"
      />

      <keyword-list-input
        :disabled="!isEditable"
        :errors="fields.errors.tags"
        :label="$t('fields.tags')"
        v-model="fields.values.tags"
      />
    </fieldset>

    <!-- Selectors -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.selectors')}}</legend>

      <color-input
        :disabled="!isEditable"
        :errors="fields.errors.color"
        :label="$t('fields.color')"
        v-model="fields.values.color"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.size"
        :label="$t('fields.size')"
        type="text"
        v-model="fields.values.size"
      />

      <memory-input
        :disabled="!isEditable"
        :errors="fields.errors.memory"
        :label="$t('fields.memory')"
        v-model="fields.values.memory"
      />

      <memory-input
        :disabled="!isEditable"
        :errors="fields.errors.storage"
        :label="$t('fields.storage')"
        v-model="fields.values.storage"
      />
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.securityInformationUrl"
        :label="$t('fields.securityInformationUrl')"
        type="text"
        v-model.number="fields.values.securityInformationUrl"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.altName"
        :label="$t('fields.altName')"
        type="text"
        v-model="fields.values.altName"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.deliveredItems"
        :label="$t('fields.deliveredItems')"
        v-model="fields.values.deliveredItems"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.shortUsp"
        :label="$t('fields.shortUsp')"
        v-model="fields.values.shortUsp"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.usps"
        :label="$t('fields.usps')"
        v-model="fields.values.usps"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.summaryTexts"
        :label="$t('fields.summaryTexts')"
        v-model="fields.values.summaryTexts"
      />

      <dimensions-input
        :disabled="!isEditable"
        :errors="fields.errors.dimensions"
        :label="$t('fields.dimensions')"
        v-model="fields.values.dimensions"
      />

      <weight-input
        :disabled="!isEditable"
        :errors="fields.errors.weight"
        :label="$t('fields.weight')"
        v-model="fields.values.weight"
      />
    </fieldset>

    <!-- CommonCharger -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.commonCharger')}}</legend>


      <Checkbox
        :disabled="!isEditable"
        :errors="fields.errors.commonCharger.possible"
        :filter="{ bundle: false }"
        :label="$t('fields.commonCharger.possible')"
        v-model="fields.values.commonCharger.possible"
      /> 

      <Checkbox
        :disabled="!isEditable"
        :errors="fields.errors.commonCharger.included"
        :filter="{ bundle: false }"
        :label="$t('fields.commonCharger.included')"
        v-model="fields.values.commonCharger.included"
      /> 
      
      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.commonCharger.minWattage"
        :label="$t('fields.commonCharger.minWattage')"
        step="0.5"
        min="0.0"
        type="number"
        v-model.number="fields.values.commonCharger.minWattage"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.commonCharger.maxWattage"
        :label="$t('fields.commonCharger.maxWattage')"
        step="0.5"
        min="0.0"
        type="number"
        v-model.number="fields.values.commonCharger.maxWattage"
      />

      <Checkbox
        :disabled="!isEditable"
        :errors="fields.errors.commonCharger.usbPd"
        :filter="{ bundle: false }"
        :label="$t('fields.commonCharger.usbPd')"
        v-model="fields.values.commonCharger.usbPd"
      /> 
    </fieldset>

    <!-- Style/Design -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.design')}}</legend>

      <product-style-combobox
        :disabled="!isEditable"
        :errors="fields.errors.style"
        :filter="{ bundle: false }"
        :label="$t('fields.style')"
        v-model="fields.values.style"
      />

      <radio-group-input
        :collection="productCustomizabilities"
        :disabled="!isEditable"
        :errors="fields.errors.customizability"
        :includeBlank="false"
        :label="$t('fields.customizability')"
        v-model="fields.values.customizability"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.designName"
        :label="$t('fields.designName')"
        type="text"
        v-model="fields.values.designName"
      />

      <product-design-artist-combobox
        :disabled="!isEditable"
        :errors="fields.errors.designArtist"
        :filter="{ bundle: false }"
        :label="$t('fields.designArtist')"
        v-model="fields.values.designArtist"
      />

      <product-design-category-combobox
        :disabled="!isEditable"
        :errors="fields.errors.designCategories"
        :filter="{ bundle: false }"
        :label="$t('fields.designCategories')"
        multiple
        v-model="fields.values.designCategories"
      />

      <attachment-input
        :disabled="!isEditable"
        :errors="fields.errors.designImage"
        :label="$t('fields.designImage')"
        attachment-type="IMAGE"
        v-model="fields.values.designImage"
      />
    </fieldset>

    <!-- Features -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.features')}}</legend>

      <features-manager
        :disabled="!isEditable"
        :errors="fields.errors.features"
        v-model="fields.values.features"
      />
    </fieldset>

    <!-- SEO -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.seo')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.slug"
        :label="$t('fields.slug')"
        :placeholder="slugPlaceholder"
        type="text"
        v-model="fields.values.slug"
      />

      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.description"
        :label="$t('fields.description')"
        rows="5"
        v-model="fields.values.description"
      />

      <keyword-list-input
        :disabled="!isEditable"
        :errors="fields.errors.aliases"
        :label="$t('fields.aliases')"
        v-model="fields.values.aliases"
      />

      <keyword-list-input
        :disabled="!isEditable"
        :errors="fields.errors.keywords"
        :label="$t('fields.keywords')"
        v-model="fields.values.keywords"
      />
    </fieldset>

    <!-- Legal Texts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.legalTexts')}}</legend>

      <legal-text-manager
        :disabled="!isEditable"
        :errors="fields.errors.legalTexts"
        v-model="fields.values.legalTexts"
      />
    </fieldset>

    <!-- Media -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.media')}}</legend>

      <attachment-manager
        :disabled="!isEditable"
        :errors="fields.errors.media"
        attachment-type="MEDIA_OBJECT"
        v-model="fields.values.media"
      />
    </fieldset>

    <Checkbox v-model="fields.values.alwaysDisplayed" :label="'Always Displayed?'"/>

    <div class="uk-margin-medium-top">
      <submit-button :disabled="!isSubmittable" type="primary" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import AttachmentInput from '@/components/AttachmentInput';
import AttachmentManager from '@/components/AttachmentManager';
import Authentication from '@/mixins/Authentication';
import CancelButton from '@/components/CancelButton';
import ColorInput from '@/components/ColorInput';
import CommonButton from '@/components/CommonButton';
import DimensionsInput from '@/components/DimensionsInput';
import FeaturesManager from '@/components/FeaturesManager';
import Form from '@/mixins/Form';
import KeywordListInput from '@/components/KeywordListInput';
import LegalTextManager from '@/components/LegalTextManager';
import MemoryInput from '@/components/MemoryInput';
import ProductDesignArtistCombobox
  from '@/components/ProductDesignArtistCombobox';
import ProductDesignCategoryCombobox
  from '@/components/ProductDesignCategoryCombobox';
import ProductFamilyCombobox from '@/components/ProductFamilyCombobox';
import ProductStyleCombobox from '@/components/ProductStyleCombobox';
import RadioGroupInput from '@/components/RadioGroupInput';
import ReadonlyFormField from '@/components/ReadonlyFormField';
import slugQuery from '@/api/queries/slug.graphql';
import SubmitButton from '@/components/SubmitButton';
import TextareaInput from '@/components/TextareaInput';
import TextInput from '@/components/TextInput';
import TextListInput from '@/components/TextListInput';
import TextPlaceholder from '@/components/TextPlaceholder';
import WeightInput from '@/components/WeightInput';
import Checkbox from '@/components/Checkbox';


const PRODUCT_CUSTOMIZABILITIES = ['NONE', 'PARTIAL', 'FULL'];

export default {
  name: 'standard-product-form',
  mixins: [
    Authentication,
    Form({
      aliases: { type: Form.Types.Array },
      altName: true,
      articleNumber: { required: true },
      color: { type: Form.Types.NullableObject },
      commonCharger: { type: Form.Types.Object, default: {} },
      customizability: { default: () => PRODUCT_CUSTOMIZABILITIES[0] },
      deliveredItems: { type: Form.Types.Array },
      description: true,
      designName: true,
      designArtist: { type: Form.Types.Reference },
      designCategories: {
        type: Form.Types.ReferenceList,
        dumpTo: 'designCategoryIds',
      },
      designImage: { type: Form.Types.Reference },
      dimensions: { type: Form.Types.NullableObject },
      ean: { required: true },
      family: { type: Form.Types.Reference, required: true },
      features: { type: Form.Types.Array },
      name: { dumpTo: null },
      legalTexts: { type: Form.Types.ReferenceList },
      keywords: { type: Form.Types.Array },
      manufacturerProductCode: true,
      media: { type: Form.Types.ReferenceList, dumpTo: 'mediaObjectIds' },
      memory: { type: Form.Types.NullableObject },
      securityInformationUrl: true,
      shortUsp: { type: Form.Types.Array },
      size: true,
      slug: true,
      storage: { type: Form.Types.NullableObject },
      style: { type: Form.Types.Reference },
      summaryTexts: { type: Form.Types.Array },
      tags: { type: Form.Types.Array },
      usps: { type: Form.Types.Array },
      variantName: true,
      weight: { type: Form.Types.NullableObject },
      alwaysDisplayed: true,
    }),
  ],
  components: {
    AttachmentInput,
    AttachmentManager,
    CancelButton,
    ColorInput,
    CommonButton,
    DimensionsInput,
    FeaturesManager,
    KeywordListInput,
    LegalTextManager,
    MemoryInput,
    ProductDesignArtistCombobox,
    ProductDesignCategoryCombobox,
    ProductFamilyCombobox,
    ProductStyleCombobox,
    ReadonlyFormField,
    RadioGroupInput,
    SubmitButton,
    TextareaInput,
    TextInput,
    TextListInput,
    TextPlaceholder,
    WeightInput,
    Checkbox,
  },
  data() {
    return {
      productCustomizabilities: PRODUCT_CUSTOMIZABILITIES.map(value => ({
        value,
        label: this.$t(`productCustomizabilities.${value}`),
      })),
    };
  },
  props: {
    productFamily: Object,
  },
  mounted() {
    this.$refs.variantNameField.focus();
    this.setProductFamilyFromProp();
  },
  watch: {
    productFamily() {
      this.setProductFamilyFromProp();
    },
  },
  apollo: {
    slugPlaceholder: {
      query: slugQuery,
      variables() {
        return { value: this.name };
      },
      fetchPolicy: 'network-only',
      debounce: { wait: 500 },
      update({ slug }) {
        return slug;
      },
      skip() {
        return !this.name;
      },
    },
  },
  computed: {
    familyName() {
      const family = this.fields.values.family;
      return family && family.name;
    },
    name() {
      const variantName = this.fields.values.variantName || '';
      if (!this.familyName) return null;
      return `${this.familyName} ${variantName}`.trim();
    },
  },
  methods: {
    setProductFamilyFromProp() {
      if (!this.fields.values.family) {
        this.fields.values.family = this.productFamily;
      }
    },
  },
  i18n: {
    messages: {
      de: {
        placeholders: {
          variantName: 'unbenannt',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// StandardProductForm.vue?5d872ce4